/**
 * Conflict Detection Workflow
 *
 * This workflow detects calendar conflicts:
 * 1. Triggers on calendar event creation/update
 * 2. Fetches other events for the same day
 * 3. Detects time overlaps
 * 4. Alerts user to scheduling conflicts
 */

import type { Workflow } from './types';

// =============================================================================
// WORKFLOW DEFINITION
// =============================================================================

export const conflictDetectionWorkflow: Workflow = {
  id: 'conflict-detection',
  name: 'Rilevamento Conflitti Calendario',
  description: 'Rileva automaticamente sovrapposizioni e conflitti tra eventi del calendario, avvisando immediatamente l\'utente per permettere ripianificazioni',
  trigger: {
    type: 'event',
    config: {
      // Trigger when calendar event is created or updated
      eventTypes: ['entity.created', 'entity.updated'],
      entityType: 'calendar_event',
      // Only check if event has a time (not all-day events)
      conditions: {
        field: 'hasTime',
        operator: 'equals',
        value: true,
      },
    },
  },
  steps: [
    {
      id: 'fetch_events',
      name: 'Recupera Eventi',
      type: 'custom',
      config: {
        // Fetch all events for the same day
        action: 'fetch_calendar_events',
        dateRange: {
          start: '{eventDate}',
          end: '{eventDate}',
        },
        // Exclude the current event being checked
        excludeIds: ['{currentEventId}'],
        includeTypes: ['meeting', 'appointment', 'videocall', 'event'],
      },
      next: ['detect_overlaps'],
    },
    {
      id: 'detect_overlaps',
      name: 'Rileva Sovrapposizioni',
      type: 'custom',
      config: {
        // Check for time overlaps between events
        action: 'detect_time_overlaps',
        checkFields: {
          startTime: '{eventStartTime}',
          endTime: '{eventEndTime}',
        },
        // Consider buffer time between events (e.g., 15 min)
        bufferMinutes: 15,
        conflictTypes: [
          'full_overlap',      // Event completely overlaps another
          'partial_overlap',   // Events partially overlap
          'adjacent_no_buffer' // Events back-to-back with no buffer
        ],
      },
      next: ['send_notification'],
      condition: {
        field: 'hasConflicts',
        operator: 'equals',
        value: true,
      },
    },
    {
      id: 'send_notification',
      name: 'Avvisa Conflitto',
      type: 'send_notification',
      config: {
        type: 'in_app',
        message: '⚠️ Conflitto calendario rilevato! L\'evento "{eventTitle}" ({eventTime}) si sovrappone con {conflictCount} altri eventi.',
        priority: 'high',
        includeDetails: {
          conflicts: true,
          suggestedTimes: false,
        },
        action: {
          label: 'Risolvi Conflitti',
          handler: 'open_calendar_conflict_resolver',
        },
      },
    },
  ],
  enabled: true,
  version: 1,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Event time range interface
 */
interface TimeRange {
  start: Date;
  end: Date;
}

/**
 * Check if two time ranges overlap
 */
export function checkTimeOverlap(
  range1: TimeRange,
  range2: TimeRange,
  bufferMinutes = 0
): boolean {
  // Add buffer time to the ranges
  const bufferedRange1 = {
    start: new Date(range1.start.getTime() - bufferMinutes * 60000),
    end: new Date(range1.end.getTime() + bufferMinutes * 60000),
  };

  // Check for overlap: range1 starts before range2 ends AND range1 ends after range2 starts
  return (
    bufferedRange1.start < range2.end && bufferedRange1.end > range2.start
  );
}

/**
 * Detect all overlapping events
 */
export function detectOverlappingEvents(
  targetEvent: { startTime: string; endTime: string; id: string },
  otherEvents: Array<{ startTime: string; endTime: string; id: string; title: string }>,
  bufferMinutes = 0
): Array<{ eventId: string; title: string; overlapType: string }> {
  const targetRange = {
    start: new Date(targetEvent.startTime),
    end: new Date(targetEvent.endTime),
  };

  const conflicts: Array<{ eventId: string; title: string; overlapType: string }> = [];

  for (const event of otherEvents) {
    if (event.id === targetEvent.id) continue;

    const eventRange = {
      start: new Date(event.startTime),
      end: new Date(event.endTime),
    };

    if (checkTimeOverlap(targetRange, eventRange, bufferMinutes)) {
      // Determine overlap type
      let overlapType = 'partial_overlap';

      if (
        targetRange.start >= eventRange.start &&
        targetRange.end <= eventRange.end
      ) {
        overlapType = 'full_overlap';
      } else if (
        Math.abs(targetRange.end.getTime() - eventRange.start.getTime()) < bufferMinutes * 60000
      ) {
        overlapType = 'adjacent_no_buffer';
      }

      conflicts.push({
        eventId: event.id,
        title: event.title,
        overlapType,
      });
    }
  }

  return conflicts;
}

/**
 * Format time for display
 */
export function formatEventTime(startTime: string, endTime: string): string {
  const start = new Date(startTime).toLocaleTimeString('it-IT', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const end = new Date(endTime).toLocaleTimeString('it-IT', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return `${start} - ${end}`;
}
